import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  PASSWORD_REQUIREMENTS_HINT,
  setPasswordFormSchema,
  type SetPasswordFormValues,
} from '@groundly/shared';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { checkPasswordSetupLink, useSetUpPassword } from '../api/password-setups.js';
import { Button, InlineAction } from '../components/Button.js';
import { DangerAlert } from '../components/DangerAlert.js';
import { PageHeading } from '../components/PageHeading.js';
import { CheckingSession } from '../components/SessionStates.js';
import { TextField } from '../components/TextField.js';
import text from '../components/pre-account-text.module.css';
import { PreAccountCard, PreAccountFrame } from '../frame/PreAccountFrame.js';

/**
 * Set your password: the page the link in an added Account User's verification
 * email opens (users-and-permissions `ux.md`, Set your password screen;
 * `technical.md`, Dashboard, `/set-password`).
 *
 * It works whatever session the browser holds, as sign-up's `/verify-email`
 * does: whoever has the link controls the address. A link that can't be used
 * goes to sign-up's Get a new verification email page. Every outcome replaces
 * the page in the history, so the token doesn't stay there.
 */
const searchSchema = z.object({ token: z.string().optional() });

export const Route = createFileRoute('/set-password')({
  validateSearch: (search: Record<string, unknown>) => {
    const parsed = searchSchema.safeParse(search);
    return parsed.success ? parsed.data : {};
  },
  component: SetPasswordPage,
});

type PageState = 'checking' | 'form' | 'check-failed';

function SetPasswordPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const [state, setState] = useState<PageState>('checking');
  // Once per page: React's development double-run must not check twice.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const goToResend = () =>
      navigate({ to: '/verify-email/resend', search: { link: 'invalid' }, replace: true });

    void (async () => {
      if (token === undefined || token === '') {
        await goToResend();
        return;
      }
      try {
        const answer = await checkPasswordSetupLink(token);
        if (answer.status === 'valid') setState('form');
        else await goToResend();
      } catch {
        // No answer: the link may still work, so it stays and the person is
        // told to open it again, as on the verification link page.
        setState('check-failed');
      }
    })();
  }, [navigate, token]);

  if (state === 'checking') return <CheckingSession />;

  if (state === 'check-failed' || token === undefined) {
    return (
      <PreAccountFrame>
        <PreAccountCard head={<PageHeading>Set your password</PageHeading>}>
          <DangerAlert title="Couldn't open this link">
            Groundly couldn&apos;t be reached. Open the link again in a moment.
          </DangerAlert>
        </PreAccountCard>
      </PreAccountFrame>
    );
  }

  return <SetPasswordForm token={token} />;
}

function SetPasswordForm({ token }: { token: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setUp = useSetUpPassword();
  const [showPassword, setShowPassword] = useState(false);
  const [failed, setFailed] = useState(false);
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    setFocus,
    formState: { errors },
  } = useForm<SetPasswordFormValues>({
    resolver: zodResolver(setPasswordFormSchema),
    defaultValues: { password: '' },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const onSubmit = handleSubmit(async (values) => {
    if (setUp.isPending || done) return;
    setFailed(false);
    try {
      await setUp.mutateAsync({ token, password: values.password });
    } catch (error) {
      if (error instanceof ApiError && error.code === 'VALIDATION_FAILED') {
        if (error.fields?.some((field) => field.path === 'token')) {
          await navigate({
            to: '/verify-email/resend',
            search: { link: 'invalid' },
            replace: true,
          });
          return;
        }
        const passwordError = error.fields?.find((field) => field.path === 'password');
        if (passwordError) {
          setError('password', { message: passwordError.message });
          setFocus('password');
          return;
        }
      }
      setFailed(true);
      return;
    }
    setDone(true);
    // Signed in now. No page may show anything cached before
    // (`patterns/frontend-conventions.md`, 1); `/` asks where they land.
    queryClient.clear();
    await navigate({ href: '/', replace: true });
  });

  const isBusy = setUp.isPending || done;

  return (
    <PreAccountFrame>
      <PreAccountCard
        as="form"
        labelledBy="set-password-heading"
        onSubmit={(event) => void onSubmit(event)}
        head={<PageHeading id="set-password-heading">Set your password</PageHeading>}
      >
        {failed ? (
          <DangerAlert title="Your password wasn't saved.">
            Check your internet connection and try again.
          </DangerAlert>
        ) : null}

        <p className={text.message}>Choose a password to finish setting up your login.</p>

        <TextField
          {...register('password')}
          id="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          hint={PASSWORD_REQUIREMENTS_HINT}
          error={errors.password?.message}
          labelAction={
            <InlineAction
              aria-controls="password"
              onClick={() => setShowPassword((shown) => !shown)}
              icon={
                showPassword ? (
                  <EyeOff aria-hidden="true" strokeWidth={1.75} width={14} height={14} />
                ) : (
                  <Eye aria-hidden="true" strokeWidth={1.75} width={14} height={14} />
                )
              }
            >
              {showPassword ? 'Hide password' : 'Show password'}
            </InlineAction>
          }
        />

        <Button type="submit" variant="primary" block disabled={isBusy}>
          {isBusy ? 'Saving' : 'Set password and continue'}
        </Button>
      </PreAccountCard>
    </PreAccountFrame>
  );
}
